import { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import styled from "@emotion/styled";

import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import CircularProgress from "@mui/material/CircularProgress";

import PersonForm from "../components/PersonForm";
import PersonFormArray from "../components/PersonFormArray";
import DisciplineFormArray from "../components/DisciplineFormArray";
import FormConfigBlocks from "../components/FormConfigBlocks";
import PersonalDataConditions from "../components/PersonalDataConditions";
import ErrorModal from "../components/ErrorModal";

import { inscriptionPost } from "../services/inscription-api";
import { checkActivePeriod } from "../services/courses-api";
import { featureFlagGetEnableForm } from "../services/featureFlag-api";
import { getFormConfig } from "../services/formConfig-api";
import { safeUrl, useSchool } from "../school/SchoolContext";
import {
  DEFAULT_FORM_CONFIG,
  normalizeFormConfig,
} from "../config/defaultFormConfig";

function Registration() {
  const methods = useForm();
  const { formState: { isSubmitted, isValid } } = methods;
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [checkingPeriod, setCheckingPeriod] = useState(true);
  const [formConfig, setFormConfig] = useState(DEFAULT_FORM_CONFIG);
  const [acceptedConditions, setAcceptedConditions] = useState({});

  const school = useSchool();

  useEffect(() => {
    Promise.all([
      checkActivePeriod(),
      featureFlagGetEnableForm(),
      // config indisponible : on garde le texte par défaut plutôt que de bloquer le formulaire
      getFormConfig().catch(() => DEFAULT_FORM_CONFIG),
    ])
      .then(([, formEnabled, config]) => {
        if (!formEnabled) {
          navigate("/not-available");
        } else {
          setFormConfig(normalizeFormConfig(config));
          setCheckingPeriod(false);
        }
      })
      .catch(() => {
        navigate("/not-available");
      });
  }, [navigate]);

  const onSubmit = (adultData) => {
    //Post inscription
    setLoading(true);
    inscriptionPost(adultData)
        .then((response) => {
          setData(response.data);
          navigate("/success");
        })
        .catch((error) => {
          // Formulaire fermé entre l'ouverture de la page et l'envoi : page dédiée.
          if (error?.response?.data?.code === "FORM_CLOSED") {
            navigate("/not-available");
            return;
          }
          setShowErrorModal(true);
          navigate("/error");
        })
        .finally(() => {
          setLoading(false);
        });
  };

  const onCloseErrorModal = () => {
    setShowErrorModal(false);
  };

  const onToggleCondition = (blockIndex) => {
    setAcceptedConditions((previous) => ({
      ...previous,
      [blockIndex]: !previous[blockIndex],
    }));
  };

  const allConditionsAccepted = formConfig.blocks.every(
    (block, index) => block.type !== "CONDITION" || !!acceptedConditions[index]
  );

  if (checkingPeriod) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );
  }

  return (
    <FormProvider {...methods}>
      <ErrorModal isOpen={showErrorModal} onClose={onCloseErrorModal} />

      <Container>
        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          <StyledTitle component="h1" variant="h3" align="center">
            {formConfig.title}
          </StyledTitle>
          {formConfig.subtitle && (
            <StyledTitle component="h1" variant="h3" align="center">
              {formConfig.subtitle}
            </StyledTitle>
          )}

          <StyledTitle component="h1" variant="h4" align="center">
            <FormattedMessage id="person_detail" />
          </StyledTitle>

          <Block>
            <PersonForm />
          </Block>

          {school.maxLegalTutors > 0 && (
            <>
              <StyledTitle component="h1" variant="h4" align="center">
                <FormattedMessage id="responsible" />
              </StyledTitle>

              <StyledText align="center">
                <FormattedMessage id="responsible_info" />
              </StyledText>

              <PersonFormArray
                namePrefix={"legalTutors"}
                min={0}
                max={school.maxLegalTutors}
                isTutor={true}
              />
            </>
          )}

          <StyledTitle
            component="h1"
            variant="h4"
            align="center"
            sx={{ marginBottom: "16px" }}
          >
            <FormattedMessage id="discipline" />
          </StyledTitle>

          <Block>
            <DisciplineFormArray namePrefix={"subscribedCourses"} />
          </Block>

          <FormConfigBlocks
            blocks={formConfig.blocks}
            acceptedConditions={acceptedConditions}
            onToggleCondition={onToggleCondition}
          />

          {isSubmitted && !isValid && (
              <Alert severity="error" variant="outlined" sx={{ marginBottom: "16px" }}>
                <AlertTitle>
                  <FormattedMessage id="form_invalid_alert_title" />
                </AlertTitle>

                <FormattedMessage id="form_invalid_alert" />
              </Alert>
          )}

          <StyledLoadingButton
            fullWidth
            variant="contained"
            size="small"
            type="submit"
            loading={loading}
            disabled={!allConditionsAccepted}
          >
            <FormattedMessage id="register" />
          </StyledLoadingButton>

          <PersonalDataConditions
            rgpdUrl={safeUrl(school.privacyUrl)}
            copyrightUrl={safeUrl(school.imageRightsUrl)}
          />
        </Box>
      </Container>
    </FormProvider>
  );
}

const Container = styled.div`
  padding-bottom: 32px;
  padding: 0 16px;
  max-width: 620px;
  margin: auto;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`;

// fix form overlapping due to the grid style
const Block = styled.div`
  div {
    margin-top: 0;
  }
`;

const StyledTitle = styled(Typography)`
  margin-top: 32px;
  color: #575757;
`;

const StyledText = styled(Typography)`
  margin: 24px 0;
  color: #6c6c6c;
`;

const StyledLoadingButton = styled(LoadingButton)`
  margin-top: 16px;
  height: 40px;
`;

export default Registration;
